import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { queryCache } from 'react-query';
import { Poppins } from 'src/common';
import Loader from 'src/components/Loader/Loader';
import Button from 'src/components/form/Button';
import { useAuth } from 'src/state/auth';
import { useReportDownloadUrlMutation } from 'src/graphql/_generated';
import { QKeys } from 'src/api/types';

const FullScreen = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.white};
  gap: 16px;
  padding: 24px;
  text-align: center;
`;

const LoaderWrap = styled.div`
  width: 140px;
  height: 50px;
`;

const SubtitleWrap = styled.div`
  max-width: 420px;
  color: ${({ theme }) => theme.colors.cflowerBlue};
`;

const ReportDownloadPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { isSignedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [requestDownloadUrl, { error, data, called }] = useReportDownloadUrlMutation();

  useEffect(() => {
    if (!authLoading && isSignedIn && jobId && !called) {
      requestDownloadUrl({ variables: { jobId } });
    }
  }, [authLoading, isSignedIn, jobId, called, requestDownloadUrl]);

  useEffect(() => {
    if (data?.reportDownloadUrl) {
      // BE wrote an audit row for this download — invalidate the cache so
      // when the user navigates back to /report-centre the AUDIT section
      // refetches and shows the new entry.
      queryCache.invalidateQueries([QKeys.Audit]);
      // `replace` keeps this transient page out of the back-history.
      window.location.replace(data.reportDownloadUrl);
    }
  }, [data]);

  if (authLoading) {
    return (
      <FullScreen>
        <LoaderWrap>
          <Loader size="large" />
        </LoaderWrap>
      </FullScreen>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/auth" replace />;
  }

  if (!jobId) {
    return (
      <ErrorState
        title="Invalid download link"
        subtitle="The report ID is missing from the URL. Open the Report Centre to find your reports."
        onBack={() => navigate('/report-centre')}
      />
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Could not fetch the report"
        subtitle={error.graphQLErrors?.[0]?.message || 'Please try again from the Report Centre.'}
        onBack={() => navigate('/report-centre')}
      />
    );
  }

  return (
    <FullScreen>
      <LoaderWrap>
        <Loader size="large" />
      </LoaderWrap>
      <Poppins px={20} weight={500}>
        Fetching report...
      </Poppins>
    </FullScreen>
  );
};

interface ErrorStateProps {
  title: string;
  subtitle: string;
  onBack: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ title, subtitle, onBack }) => (
  <FullScreen>
    <Poppins px={24} weight={600}>
      {title}
    </Poppins>
    <SubtitleWrap>
      <Poppins px={14}>{subtitle}</Poppins>
    </SubtitleWrap>
    <Button primary onClick={onBack} data-cy="back-to-report-centre">
      BACK TO REPORT CENTRE
    </Button>
  </FullScreen>
);

export default ReportDownloadPage;
